import React, { useState } from 'react';

import { Box, Checkbox, Chip, Skeleton, Typography } from '@mui/material';
import BankingCard, { Last4Digits } from '@apigrowfinance/pbolt-banking-card';
import { Button } from '@apigrowfinance/pbolt-button';
import { Card, CardBody } from '@apigrowfinance/pbolt-card';
import { StatementStrikethrough } from '@apigrowfinance/pbolt-icons';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import pismo from '@assets/img/pismo.png';
import { CustomerCard } from '@clients/customersClient';
import cardAltText from '@messages/cardAltText';
import cardStatuses from '@messages/cardTranslations';
import getEnumTranslation from '@utils/getEnumTranslation';

interface CardSelectionProps {
    cards: CustomerCard[];
    cardsLoading: boolean;
    goToNextStep?: () => void;
}

const CardSelection = ({
    cards,
    cardsLoading,
    goToNextStep,
}: CardSelectionProps) => {
    const { theme } = useTheme();
    const { formatMessage } = useIntl();
    const {
        control,
        formState: { isDirty },
    } = useFormContext();

    const successStatus = ['CREATED', 'NORMAL', 'PENDING'];

    const [selectedIds, setSelectedIds] = useState([]);

    const onClick = (id: number) => {
        if (selectedIds.includes(id)) {
            setSelectedIds((selectedIds) =>
                selectedIds.filter((item) => item !== id),
            );
        } else {
            setSelectedIds((selectedIds) => [...selectedIds, id]);
        }
    };

    const isSelected = (id: number) => selectedIds.includes(id);

    return (
        <>
            <Card
                sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    py: 6,
                }}>
                <Typography
                    variant="h5"
                    sx={{
                        color: theme?.custom?.brand?.textMain,
                    }}>
                    <FormattedMessage
                        id="CardSelection.selectCards"
                        defaultMessage="Select the cards you want to add the control to below:"
                    />
                </Typography>
                <Button
                    disabled={!isDirty}
                    onClick={goToNextStep}
                    name="positive"
                    role="button"
                    variant="contained"
                    disableElevation
                    data-testid="card-selection-form-next-button"
                    sx={{
                        px: 8,
                        textTransform: 'none',
                    }}>
                    Next
                </Button>
            </Card>
            {cardsLoading && (
                <Card paddingSize="medium" isLoading={cardsLoading}>
                    <CardBody direction="row">
                        <Box width={300} sx={{ mr: 2.3 }}>
                            <BankingCard
                                expiryDate="12/12"
                                last4Digits="1234"
                                printedName="Loading cards"
                                isLoading={cardsLoading}
                                fontScale={0.75}
                                foregroundLogo={{
                                    image:
                                        theme?.custom?.brand?.brandImage ||
                                        pismo,
                                    altText:
                                        theme?.custom?.brand?.brandName ||
                                        getEnumTranslation(
                                            'cardForegroundAltText',
                                            {
                                                messages: cardAltText,
                                                formatMessage,
                                            },
                                        ),
                                }}
                                backgroundLogo={{
                                    image: theme?.custom?.brand
                                        ?.brandImageSmall,
                                    altText:
                                        theme?.custom?.brand?.brandName ||
                                        getEnumTranslation(
                                            'cardBackgroundAltText',
                                            {
                                                messages: cardAltText,
                                                formatMessage,
                                            },
                                        ),
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-evenly',
                            }}>
                            <Box>
                                <Chip
                                    data-testid="card-type"
                                    sx={{
                                        borderRadius: '8px',
                                    }}
                                    size="small"
                                    label={<Skeleton width={80} />}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}>
                                <Typography
                                    data-testid="card-title"
                                    variant="h4"
                                    sx={{
                                        color: theme?.custom?.brand?.textMain,
                                    }}>
                                    <Skeleton width={120} />
                                </Typography>
                                <Checkbox name="card_ids" />
                            </Box>
                            <Box display="inline-block">
                                <Box
                                    display="inline-block"
                                    sx={{
                                        width: '6px',
                                        height: '6px',
                                        background: theme.palette.success.main,
                                        borderRadius: '50%',
                                        verticalAlign: 'middle',
                                    }}
                                />
                                <Box display="inline-block">
                                    <Typography
                                        variant="overline"
                                        data-testid="card-status"
                                        sx={{
                                            ml: 0.75,
                                        }}
                                        component="span">
                                        <Skeleton width={80} />
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </CardBody>
                </Card>
            )}
            {cards?.length &&
                cards?.map((card) => {
                    const statusDisplay =
                        card?.status &&
                        getEnumTranslation(card?.status.toLocaleUpperCase(), {
                            messages: cardStatuses,
                        });

                    return (
                        <Controller
                            data-testid={`${card?.name}-checkbox`}
                            key={card?.id}
                            name="card_ids"
                            defaultValue={[]}
                            control={control}
                            render={({ field }) => (
                                <Card
                                    isSelected={isSelected(card?.id)}
                                    onClick={() => {
                                        if (isSelected(card?.id)) {
                                            field.onChange(
                                                field.value.filter(
                                                    (item: number) =>
                                                        item !== card?.id,
                                                ),
                                            );
                                        } else {
                                            field.onChange([
                                                ...field.value,
                                                card?.id,
                                            ]);
                                        }
                                        // Update local state of selected charge plans
                                        onClick(card?.id);
                                    }}
                                    key={card?.id}
                                    paddingSize="medium"
                                    isLoading={cardsLoading}>
                                    <CardBody direction="row">
                                        <Box width={300} sx={{ mr: 2.3 }}>
                                            <BankingCard
                                                fontScale={0.75}
                                                expiryDate={
                                                    card.expiration_date
                                                }
                                                last4Digits={
                                                    card.last_4_digits as Last4Digits
                                                }
                                                printedName={card.printed_name}
                                                isLoading={cardsLoading}
                                                foregroundLogo={{
                                                    image:
                                                        theme?.custom?.brand
                                                            ?.brandImage ||
                                                        pismo,
                                                    altText:
                                                        theme?.custom?.brand
                                                            ?.brandName ||
                                                        getEnumTranslation(
                                                            'cardForegroundAltText',
                                                            {
                                                                messages:
                                                                    cardAltText,
                                                                formatMessage,
                                                            },
                                                        ),
                                                }}
                                                backgroundLogo={{
                                                    image: theme?.custom?.brand
                                                        ?.brandImageSmall,
                                                    altText:
                                                        theme?.custom?.brand
                                                            ?.brandName ||
                                                        getEnumTranslation(
                                                            'cardBackgroundAltText',
                                                            {
                                                                messages:
                                                                    cardAltText,
                                                                formatMessage,
                                                            },
                                                        ),
                                                }}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-evenly',
                                            }}>
                                            <Box>
                                                <Chip
                                                    data-testid="card-type"
                                                    sx={{
                                                        borderRadius: '8px',
                                                    }}
                                                    size="small"
                                                    label={getEnumTranslation(
                                                        card?.type,
                                                        {
                                                            messages:
                                                                cardStatuses,
                                                        },
                                                    )}
                                                />
                                            </Box>
                                            <Box
                                                data-testid={`${card?.id}-item`}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent:
                                                        'space-between',
                                                }}>
                                                <Typography
                                                    data-testid="card-title"
                                                    variant="h4"
                                                    sx={{
                                                        color: theme?.custom
                                                            ?.brand?.textMain,
                                                    }}>
                                                    {card?.name}
                                                </Typography>
                                                <Checkbox
                                                    data-testid={`${card?.id}-checkbox`}
                                                    name="card_ids"
                                                    value={card?.id}
                                                    checked={field?.value?.some(
                                                        (
                                                            existingValue: number,
                                                        ) =>
                                                            existingValue ===
                                                            card?.id,
                                                    )}
                                                    onChange={(
                                                        event,
                                                        checked,
                                                    ) => {
                                                        if (checked) {
                                                            field.onChange([
                                                                ...field.value,
                                                                Number(
                                                                    event.target
                                                                        .value,
                                                                ),
                                                            ]);
                                                        } else {
                                                            field.onChange(
                                                                field?.value?.filter(
                                                                    (
                                                                        value: number,
                                                                    ) =>
                                                                        value !==
                                                                        Number(
                                                                            event
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                ),
                                                            );
                                                        }
                                                        onClick(card?.id);
                                                    }}
                                                />
                                            </Box>
                                            <Box display="inline-block">
                                                <Box
                                                    display="inline-block"
                                                    sx={{
                                                        width: '6px',
                                                        height: '6px',
                                                        background:
                                                            successStatus.includes(
                                                                card?.status,
                                                            )
                                                                ? theme.palette
                                                                      .success
                                                                      .main
                                                                : theme.palette
                                                                      .error
                                                                      .main,
                                                        borderRadius: '50%',
                                                        verticalAlign: 'middle',
                                                    }}
                                                />
                                                <Box display="inline-block">
                                                    <Typography
                                                        variant="overline"
                                                        data-testid="card-status"
                                                        sx={{
                                                            ml: 0.75,
                                                        }}
                                                        component="span">
                                                        {statusDisplay}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </CardBody>
                                </Card>
                            )}
                        />
                    );
                })}
            {!cards?.length && !cardsLoading && (
                <Card>
                    <IllustratedMessage
                        icon={<StatementStrikethrough />}
                        messageMain={
                            <FormattedMessage
                                id="CardSelection.noCardstitle"
                                defaultMessage="No cards"
                            />
                        }
                        messageSub={
                            <FormattedMessage
                                id="CardSelection.noCardsMessage"
                                defaultMessage="There are no cards associated with this customer"
                            />
                        }
                    />
                </Card>
            )}
        </>
    );
};

export default CardSelection;
